import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import {
  AppBar,
  Badge,
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  ListItemButton,
  ListItemText,
  Toolbar,
  Typography,
} from '@mui/material';
import React, { useCallback, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { APP_NAME_FULL, APP_NAME_SHORT } from '../appMeta';
import { ImpersonationAccountInfo } from './ImpersonationBanner';
import { useAppUi } from '../context/AppUiContext';
import { LAYOUT_MAIN_GUTTER_PX, LAYOUT_SURFACE } from '../layout/layoutColors';

const DRAWER_PAPER_WIDTH = 280;

export function LayoutHeader() {
  const { user, logout, isImpersonating } = useAuth();
  const { navDrawerOpen, toggleNavDrawer } = useAppUi();
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);

  const closeAccountMenu = useCallback(() => setAccountMenuOpen(false), []);

  const userTypeLabel =
    user?.userTypeName?.trim() || user?.userTypeCode?.trim() || 'User type unavailable';
  const impersonatedUserType =
    user?.userTypeName?.trim() || user?.userTypeCode?.trim() || null;
  const impersonationChipLabel = user
    ? impersonatedUserType
      ? `As ${user.username} · ${impersonatedUserType}`
      : `As ${user.username}`
    : '';

  return (
    <AppBar
      position="sticky"
      color="inherit"
      elevation={0}
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: LAYOUT_SURFACE,
      }}
    >
      <Toolbar sx={{ width: '100%', px: `${LAYOUT_MAIN_GUTTER_PX}px` }}>
        <IconButton
          edge="start"
          color="inherit"
          aria-label="toggle navigation panel"
          aria-expanded={navDrawerOpen}
          aria-controls="app-nav-drawer"
          onClick={toggleNavDrawer}
          sx={{ mr: 1, color: 'text.primary' }}
        >
          <MenuIcon />
        </IconButton>
        <Typography
          variant="h6"
          component={RouterLink}
          to="/"
          title={APP_NAME_FULL}
          sx={{
            flexGrow: 1,
            color: 'text.primary',
            textDecoration: 'none',
            fontWeight: 600,
            '&:hover': { color: 'primary.main' },
          }}
        >
          {APP_NAME_SHORT}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {user && (
            <>
              {isImpersonating ? (
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  label={impersonationChipLabel}
                  title={impersonationChipLabel}
                  onClick={() => setAccountMenuOpen(true)}
                  data-testid="impersonation-header-chip"
                  sx={{
                    height: 24,
                    maxWidth: 280,
                    '& .MuiChip-label': { px: 1, fontSize: '0.75rem' },
                  }}
                />
              ) : null}
              <IconButton
                edge="end"
                color="inherit"
                aria-label="open account menu"
                aria-expanded={accountMenuOpen}
                aria-haspopup="true"
                onClick={() => setAccountMenuOpen(true)}
                sx={{ color: 'text.primary' }}
                data-testid="header-account-menu-button"
              >
                <Badge
                  color="warning"
                  variant="dot"
                  invisible={!isImpersonating}
                  overlap="circular"
                >
                  <AccountCircleOutlinedIcon sx={{ fontSize: 28 }} />
                </Badge>
              </IconButton>
              <Drawer
                anchor="right"
                open={accountMenuOpen}
                onClose={closeAccountMenu}
                PaperProps={{
                  sx: { width: DRAWER_PAPER_WIDTH, pt: 1 },
                  'data-testid': 'header-account-drawer',
                }}
              >
                <ImpersonationAccountInfo onExit={closeAccountMenu} />
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography variant="subtitle1" fontWeight={600} noWrap title={user.email}>
                    {user.username}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap title={user.email}>
                    {user.email}
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ py: 0.5 }}>
                  <ListItemButton
                    component={RouterLink}
                    to="/profile"
                    onClick={closeAccountMenu}
                    data-testid="header-my-profile"
                  >
                    <ListItemText primary="My profile" />
                  </ListItemButton>
                  <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      User type
                    </Typography>
                    <Typography variant="body2">{userTypeLabel}</Typography>
                  </Box>
                  <Divider />
                  <ListItemButton
                    onClick={() => {
                      closeAccountMenu();
                      logout();
                    }}
                    data-testid="header-logout"
                  >
                    <ListItemText primaryTypographyProps={{ color: 'primary' }} primary="Log out" />
                  </ListItemButton>
                </Box>
              </Drawer>
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
}
